const MINUTES_PER_HOUR = 60;
const LEVEL_TWENTY = 20;
const MINUTES_AFTER_LEVEL_TWENTY = 20 * MINUTES_PER_HOUR;

export interface ReadingLevelProgress {
  currentLevel: number;
  nextLevel: number;
  totalMinutes: number;
  minutesInCurrentLevel: number;
  minutesRequiredForNextLevel: number;
  progressPercentage: number;
}

export const minutesRequiredToReachLevel = (level: number): number => {
  const normalizedLevel = Math.max(0, Math.floor(level));
  if (normalizedLevel <= LEVEL_TWENTY) {
    return (normalizedLevel * (normalizedLevel + 1) / 2) * MINUTES_PER_HOUR;
  }
  return minutesRequiredToReachLevel(LEVEL_TWENTY)
    + (normalizedLevel - LEVEL_TWENTY) * MINUTES_AFTER_LEVEL_TWENTY;
};

export const getReadingLevel = (eligibleDurationMs: number): ReadingLevelProgress => {
  const safeDurationMs = Number.isFinite(eligibleDurationMs) ? Math.max(0, eligibleDurationMs) : 0;
  const totalMinutes = Math.floor(safeDurationMs / 60_000);
  const levelTwentyMinutes = minutesRequiredToReachLevel(LEVEL_TWENTY);

  let currentLevel = 0;
  if (totalMinutes >= levelTwentyMinutes) {
    currentLevel = LEVEL_TWENTY + Math.floor((totalMinutes - levelTwentyMinutes) / MINUTES_AFTER_LEVEL_TWENTY);
  } else {
    for (let candidate = 1; candidate <= LEVEL_TWENTY; candidate += 1) {
      if (totalMinutes < minutesRequiredToReachLevel(candidate)) break;
      currentLevel = candidate;
    }
  }

  const currentLevelStart = minutesRequiredToReachLevel(currentLevel);
  const nextLevel = currentLevel + 1;
  const nextLevelStart = minutesRequiredToReachLevel(nextLevel);
  const minutesRequiredForNextLevel = nextLevelStart - currentLevelStart;
  const minutesInCurrentLevel = totalMinutes - currentLevelStart;

  return {
    currentLevel,
    nextLevel,
    totalMinutes,
    minutesInCurrentLevel,
    minutesRequiredForNextLevel,
    progressPercentage: minutesRequiredForNextLevel > 0
      ? (minutesInCurrentLevel / minutesRequiredForNextLevel) * 100
      : 0
  };
};

export const formatReadingMinutes = (totalMinutes: number): string => {
  const safeMinutes = Number.isFinite(totalMinutes) ? Math.max(0, Math.floor(totalMinutes)) : 0;
  const hours = Math.floor(safeMinutes / MINUTES_PER_HOUR);
  const minutes = safeMinutes % MINUTES_PER_HOUR;
  if (hours === 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

export const formatDedicatedReadingTime = (totalMinutes: number): string => {
  const safeMinutes = Math.max(0, Math.floor(totalMinutes));
  if (safeMinutes % MINUTES_PER_HOUR !== 0) return `${formatReadingMinutes(safeMinutes)} dedicados à leitura`;
  const hours = safeMinutes / MINUTES_PER_HOUR;
  return `${hours} ${hours === 1 ? 'hora dedicada' : 'horas dedicadas'} à leitura`;
};
